<!--
 * @Descripttion: 会员购买弹窗（公共组件）
 *   首页顶部下拉「普通会员」与星盘页「租期已过 → 支付年租」共用同一套支付链路，
 *   保证套餐价格（/yljx/public/price）、微信内 H5 支付（h5pay）、桌面扫码支付（pay）、
 *   轮询查单（lunxun）、支付成功后写回登录态并刷新等行为完全一致，避免两处各写一份。
 * @params:
 *   visible          [Boolean] 显示控制（配合 .sync 使用）
 *   title            [String]  弹窗标题
 *   intro            [String]  弹窗顶部说明文案（不传则不显示）
 *   payButtonText    [String]  支付按钮文案
 *   successMessage   [String]  支付成功提示文案
-->
<template>
  <!--
    append-to-body 必须保留：弹窗挂载点（.el-dialog__wrapper）依赖 position: fixed 居中，
    而宿主页面若存在 `#xxx > * { position: relative }` 这类通配规则（星盘页就有），
    会把它改成 relative —— 弹窗会掉进文档流出现在页面最下方，且点击遮罩关闭失效。
    挂到 body 后既脱离宿主布局，又能正常覆盖全屏、支持点遮罩关闭。
  -->
  <el-dialog
    :title="title"
    :visible.sync="dialogVisible"
    width="80%"
    append-to-body
    :close-on-click-modal="true"
    :before-close="handleClose">
    <!-- 场景说明：星盘页续费时传入「租期已过」提示，首页不传则不渲染 -->
    <p v-if="intro" class="vip-purchase-intro">{{ intro }}</p>

    <!-- 会员到期口径：剩余时长叠加（未到期则在原到期日上再顺延一年，剩余时长完整保留） -->
    <div v-if="memberExpiryInfo" class="vip-stack-info">
      <p v-if="memberExpiryInfo.active">
        当前会员到期：<b>{{ memberExpiryInfo.currentText }}</b>
        （剩余 {{ memberExpiryInfo.remainingDays }} 天，续费后完整保留）
      </p>
      <p v-else>当前会员未开通或已过期，本次购买自今天起算一年</p>
      <p class="vip-stack-result">
        本次购买 {{ memberExpiryYears }} 年，<b>购买后到期：{{ memberExpiryInfo.stackedText }}</b>
      </p>
    </div>

    <div class="flex">
      <div
        v-for="(item, index) in payList"
        :key="item.id === undefined ? index : item.id"
        class="icelis"
        style="cursor: pointer"
        @click="onClick(item, index)"
        :class="payTimeIndex === index ? 'changes' : 'change'">
        <!--套餐名称-->
        <div style="font-size: 16px;color: #333333;font-weight: 500">{{ item.name }}</div>
        <!--套餐实际付款价格-->
        <div style="color: #C79745;font-size: 24px;font-weight: 600">{{ '￥' + item.actualTotalAmount }}</div>
        <!--套餐原价-->
        <div style="text-decoration:line-through;color: #999999">{{ '￥' + item.totalAmount }}</div>
      </div>
    </div>
    <div class="shifu">
      实付：<span style="color: #C79745;font-size: 24px;font-weight: 600">￥{{ payList[payTimeIndex].actualTotalAmount }}</span>
      <br>
      已优惠0元
    </div>
    <div class="shifu2">
      <el-button type="primary" :loading="paying" @click="zhifyu()">{{ payButtonText }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { MessageBox, Message } from 'element-ui'
import apiReq from '@t/api'
import { ensureWechatBound, invokeWechatPay, startScanPay } from '@t/utils/wechatPay'
// 登录态统一写入口 + 刷新入口：会员支付成功后写回最新 user 并广播
const userContext = require('../../../services/userContext')
const refreshUser = require('@t/utils/refreshTianxingUser')

// 会员到期口径（剩余时长叠加）：与后端 PermissionCatalog.stackedMemberExpireAt 一致
const memberExpiry = require('../../../utils/memberExpiry')

export default {
  name: 'VipPurchaseDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '购买会员'
    },
    // 顶部说明文案（例如星盘页的「尊敬的用户，您的租期已过，请重新支付年租。」）
    intro: {
      type: String,
      default: ''
    },
    payButtonText: {
      type: String,
      default: '去支付'
    },
    successMessage: {
      type: String,
      default: '恭喜您成为尊贵的高级会员，请按需使用相关计算功能模块，祝您使用愉快！'
    }
  },
  data () {
    return {
      dialogVisible: false,
      priceList: [],
      price: 0,
      payTimeIndex: 0,
      payList: [{
        name: '年卡',
        actualTotalAmount: 199,
        totalAmount: 200
      }],
      lunxunid: 0,
      timer: null,
      paying: false,
      // 会员到期口径展示：{active, currentText, stackedText, remainingDays}
      memberExpiryInfo: null,
      memberExpiryYears: memberExpiry.MEMBER_DURATION_YEARS
    }
  },
  watch: {
    // 父组件控制显示
    visible (val) {
      this.dialogVisible = val
      if (val) {
        // 每次打开都按最新登录态重算「购买后到期时间」，避免读到上次的快照
        this.refreshMemberExpiry()
      }
    },
    // el-dialog 关闭时回传给父组件（配合 :visible.sync）
    dialogVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  created () {
    this.dialogVisible = this.visible
    this.refreshMemberExpiry()
    this.fetchPriceList()
  },
  beforeDestroy () {
    this.clearTimer()
  },
  methods: {
    // 拉取套餐价格（会员套餐：subname = 'huiyuan'）
    async fetchPriceList () {
      try {
        const priceList = await apiReq.price()
        this.priceList = Array.isArray(priceList) ? priceList : []
        this.syncVipPrice()
      } catch (error) {
        console.error('[VipPurchaseDialog] 获取会员套餐价格失败:', error)
      }
    },
    resolveVipPriceItem () {
      return (this.priceList || []).find(item => item.subname === 'huiyuan') || null
    },
    // 把接口价格同步到展示用的套餐上（拿不到接口价时保留默认展示价）
    syncVipPrice () {
      const vipItem = this.resolveVipPriceItem()
      if (vipItem) {
        this.price = vipItem.price
        this.payList[0].actualTotalAmount = vipItem.price
      }
    },
    isMobile () {
      const flag = navigator.userAgent.match(/(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i)
      return !!flag
    },
    isWeixinBrowser () {
      return typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent)
    },
    // 读取登录态（支付必须带用户 id）
    readCurrentUser () {
      try {
        const raw = localStorage.getItem('user')
        return raw ? JSON.parse(raw) : null
      } catch (error) {
        return null
      }
    },
    /**
     * 按当前登录态重算会员到期口径（展示「购买后到期时间」用）：
     * 未到期 → 原到期 + 1 年（剩余时长完整保留）；未开通 / 已过期 → 今天 + 1 年。
     */
    refreshMemberExpiry () {
      const user = this.readCurrentUser() || {}
      this.memberExpiryInfo = memberExpiry.describeMemberExpiry(user.express_time, Date.now())
    },
    handleClose () {
      this.dialogVisible = false
    },
    onClick (item, index) {
      this.payTimeIndex = index
    },
    clearTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    // 轮询查单：支付成功后写回登录态并刷新页面（与首页原实现一致）
    async lunxun2 () {
      const res = await apiReq.getLunxun({ id: this.lunxunid })
      if (res && res.code === '1111') {
        this.clearTimer()
        MessageBox.close()
        // 统一写入口（写入即广播）；随后立刻拉一次权威用户信息，
        // 3 秒后的整页刷新兜底之前先把全局参数更新到位
        userContext.writeStoredTianxingUser(res.data)
        refreshUser.refreshTianxingUser({ user: res.data })
        Message.success({ message: this.successMessage, offset: 100 })
        setTimeout(function () {
          window.location.reload()
        }, 3000)
      }
    },
    // 去支付：会员套餐固定走 type=2（后端据此把 vip 置 2、express_time 置一年后）
    async zhifyu () {
      const user = this.readCurrentUser()
      if (!user || user.id === undefined || user.id === null) {
        this.$message && this.$message.error('请先登录账号后再支付')
        return
      }

      this.syncVipPrice()
      this.paying = true
      this.dialogVisible = false

      try {
        if (this.isMobile() && this.isWeixinBrowser()) {
          // 手机微信内：拉起微信公众号支付。
          // 微信支付 JSAPI 必须用该账号自己的 openid，未绑定微信的账号会先给绑定引导，
          // 不再把空参数交给微信（否则微信会弹「调用支付JSAPI缺少参数:appId」）。
          if (!ensureWechatBound()) {
            return
          }
          const request = await apiReq.h5pay({ id: user.id, type: 2 })
          if (!invokeWechatPay(request)) {
            this.clearTimer()
            return
          }
          this.lunxunid = request.data
          this.timer = setInterval(this.lunxun2, 1000)
          return
        }

        // 桌面端 / 手机非微信：展示二维码扫码支付。
        // 扫码前先确认账号已绑定微信：未绑定则引导绑定，绑定成功后就地续跑（不整页刷新）。
        await startScanPay({
          // 惰性取参：绑定成功后续跑时 users.id 已变化，必须重新读登录态
          params: () => ({ id: (this.readCurrentUser() || {}).id, type: 2 }),
          title: '会员购买',
          serviceName: '购买会员',
          price: this.price,
          imgStyle: 'width:100%',
          extraLines: [
            '<p style="color:#f00">实时转账，付款购买成功后不支持退款，请知晓！</p>'
          ],
          onOrdered: request => {
            this.lunxunid = request.data
            this.timer = setInterval(this.lunxun2, 1000)
          },
          onCancel: () => this.clearTimer()
        })
      } catch (error) {
        console.error('[VipPurchaseDialog] 发起支付失败:', error)
        this.clearTimer()
        this.$message && this.$message.error('支付发起失败，请稍后重试')
      } finally {
        this.paying = false
      }
    }
  }
}
</script>

<style scoped>
/* 顶部说明文案（续费场景使用） */
.vip-purchase-intro {
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.6;
  color: #c0392b;
  text-align: center;
}

/* 会员「剩余时长叠加」提示：购买前告知当前到期与购买后到期 */
.vip-stack-info {
  margin: 0 0 16px;
  padding: 10px 14px;
  background: #f4f7fb;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.7;
  color: #606266;
}

.vip-stack-info p {
  margin: 0;
}

.vip-stack-result {
  color: #c79745;
}

.vip-stack-result b {
  font-size: 15px;
}

/* 以下套餐 / 合计 / 按钮样式与首页原弹窗保持一致 */
.icelis {
  width: 50%;
  margin: 0 auto;
  background-color: #d6e1da;
  height: 200px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  padding: 20px;
}

.changes {
  border: 3px solid #ff8520;
}

.flex {
  display: flex;
  justify-content: space-between;
}

.shifu {
  text-align: right;
  padding: 20px 0;
}

.shifu2 {
  text-align: right;
}
</style>
